import { useState } from 'react';
import { useImages } from '../../hooks/useImages';
import { ImageGrid } from '../ui/ImageThumbnail';
import { ImageLightbox } from '../ui/ImageLightbox';

/**
 * EntryCard Component
 *
 * Generic card component for displaying any entry type.
 * Shows relevant fields based on entry type with edit/delete actions.
 */

// Local type definitions matching backend schema
// These match the database schema types from backend/src/db/schema.ts

export interface WeightEntry {
	id: string;
	userId: string;
	date: Date;
	weight: string;
	notes: string | null;
	createdAt: Date;
	updatedAt: Date;
	createdBy: string | null;
}

export interface MealEntry {
	id: string;
	userId: string;
	date: Date;
	mealType: string;
	description: string;
	carbPortions: string | null;
	proteinPortions: string | null;
	startTime: Date | null;
	endTime: Date | null;
	notes: string | null;
	createdAt: Date;
	updatedAt: Date;
	createdBy: string | null;
}

export interface ExerciseEntry {
	id: string;
	userId: string;
	date: Date;
	sessionType: string;
	exerciseType: string;
	durationMinutes: string;
	notes: string | null;
	createdAt: Date;
	updatedAt: Date;
	createdBy: string | null;
}

export interface HealthMetricEntry {
	id: string;
	userId: string;
	date: Date;
	metricType: string;
	description: string | null;
	time: Date | null;
	hasBowelMovement: string | null;
	notes: string | null;
	createdAt: Date;
	updatedAt: Date;
	createdBy: string | null;
}

export interface MedicalVisit {
	id: string;
	userId: string;
	date: Date;
	doctorName: string;
	specialty: string | null;
	hospitalClinic: string | null;
	diagnosis: string | null;
	notes: string | null;
	createdAt: Date;
	updatedAt: Date;
	createdBy: string | null;
}

export interface Prescription {
	id: string;
	userId: string;
	visitId: string | null;
	date: Date;
	medicationName: string;
	dosage: string | null;
	frequency: string | null;
	duration: string | null;
	notes: string | null;
	createdAt: Date;
	updatedAt: Date;
	createdBy: string | null;
}

export interface ActivityEntry {
	id: string;
	userId: string;
	date: Date;
	activityName: string;
	durationMinutes: string;
	notes: string | null;
	createdAt: Date;
	updatedAt: Date;
	createdBy: string | null;
}

// Union type for all entry types
export type AnyEntry = WeightEntry | MealEntry | ExerciseEntry | HealthMetricEntry | MedicalVisit | Prescription | ActivityEntry;

export type EntryType = 'weight' | 'meal' | 'exercise' | 'health' | 'visit' | 'prescription' | 'activity';

interface EntryCardProps {
	entry: AnyEntry;
	entryType: EntryType;
	onEdit: (entry: AnyEntry, type: EntryType) => void;
	onDelete: (entry: AnyEntry, type: EntryType) => void;
	canModify: boolean;
}

/**
 * Format timestamp to HH:mm time string
 */
function formatTime(date: Date | null | undefined): string {
	if (!date) return '';
	return new Date(date).toLocaleTimeString('id-ID', {
		hour: '2-digit',
		minute: '2-digit',
	});
}

/**
 * Get meal type label in Indonesian
 */
function getMealTypeLabel(mealType: string): string {
	const labels: Record<string, string> = {
		breakfast: 'Sarapan',
		snack1: 'Snack 1',
		lunch: 'Makan Siang',
		snack2: 'Snack 2',
		dinner: 'Makan Malam',
	};
	return labels[mealType] || mealType;
}

/**
 * Get session type label in Indonesian
 */
function getSessionTypeLabel(sessionType: string): string {
	const labels: Record<string, string> = {
		morning: 'Pagi',
		afternoon: 'Sore',
	};
	return labels[sessionType] || sessionType;
}

/**
 * Get metric type label in Indonesian
 */
function getMetricTypeLabel(metricType: string): string {
	const labels: Record<string, string> = {
		medication: 'Obat',
		vitamin: 'Vitamin',
		bowel: 'BAB',
		bowel_movement: 'BAB',
		symptom: 'Gejala',
	};
	return labels[metricType] || metricType;
}

/**
 * Format portion display with Unicode fraction
 */
function formatPortion(portion: string | null | undefined): string {
	if (!portion) return '0';
	const value = parseFloat(portion);
	if (isNaN(value)) return '0';
	if (value % 1 === 0.5) return `${Math.floor(value)}\u00BD`;
	return value.toString();
}

function formatWeight(value: string | number): string {
	const weight = typeof value === 'number' ? value : parseFloat(value);
	if (isNaN(weight)) {
		return typeof value === 'string' ? value : '0.00';
	}
	return weight.toFixed(2);
}

/**
 * Get entry summary for display
 */
export function getEntrySummary(entry: AnyEntry, entryType: EntryType): string {
	switch (entryType) {
		case 'weight':
			return `${formatWeight((entry as WeightEntry).weight)} kg`;
		case 'meal':
			return (entry as MealEntry).description;
		case 'exercise':
			return `${(entry as ExerciseEntry).exerciseType} - ${(entry as ExerciseEntry).durationMinutes} menit`;
		case 'health': {
			const health = entry as HealthMetricEntry;
			if (health.metricType === 'bowel' || health.metricType === 'bowel_movement') {
				return health.hasBowelMovement === 'yes' ? 'Ya' : 'Tidak';
			}
			return health.description || '';
		}
		case 'visit':
			return (entry as MedicalVisit).doctorName;
		case 'prescription':
			return (entry as Prescription).medicationName;
		case 'activity':
			return `${(entry as ActivityEntry).activityName} - ${(entry as ActivityEntry).durationMinutes} menit`;
		default:
			return '';
	}
}

export function EntryCard({
	entry,
	entryType,
	onEdit,
	onDelete,
	canModify,
}: EntryCardProps) {
	// Map entryType to the image API format
	const imageEntryType = entryType === 'health' ? 'health' : entryType;
	const { images } = useImages(imageEntryType, entry.id);
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

	const renderContent = () => {
		switch (entryType) {
			case 'weight': {
				const weightEntry = entry as WeightEntry;
				return (
					<>
						<div className="flex items-baseline gap-2">
							<span className="text-2xl font-bold text-gray-900">
								{formatWeight(weightEntry.weight)}
							</span>
							<span className="text-gray-500">kg</span>
						</div>
						{weightEntry.notes && (
							<p className="text-sm text-gray-600 mt-1">{weightEntry.notes}</p>
						)}
					</>
				);
			}
			case 'meal': {
				const mealEntry = entry as MealEntry;
				return (
					<>
						<div className="flex items-center gap-2 mb-2">
							<span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
								{getMealTypeLabel(mealEntry.mealType)}
							</span>
							{mealEntry.startTime && (
								<span className="text-xs text-gray-500">
									{formatTime(mealEntry.startTime)}
									{mealEntry.endTime && ` - ${formatTime(mealEntry.endTime)}`}
								</span>
							)}
						</div>
						<p className="text-gray-900 font-medium">{mealEntry.description}</p>
						<div className="flex gap-4 mt-2 text-sm text-gray-600">
							<span>Karbo: {formatPortion(mealEntry.carbPortions)} porsi</span>
							<span>Protein: {formatPortion(mealEntry.proteinPortions)} porsi</span>
						</div>
						{mealEntry.notes && (
							<p className="text-sm text-gray-500 mt-1 italic">{mealEntry.notes}</p>
						)}
					</>
				);
			}
			case 'exercise': {
				const exerciseEntry = entry as ExerciseEntry;
				return (
					<>
						<div className="flex items-center gap-2 mb-2">
							<span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
								{getSessionTypeLabel(exerciseEntry.sessionType)}
							</span>
						</div>
						<p className="text-gray-900 font-medium">{exerciseEntry.exerciseType}</p>
						<p className="text-sm text-gray-600 mt-1">
							Durasi: {exerciseEntry.durationMinutes} menit
						</p>
						{exerciseEntry.notes && (
							<p className="text-sm text-gray-500 mt-1 italic">{exerciseEntry.notes}</p>
						)}
					</>
				);
			}
			case 'health': {
				const healthEntry = entry as HealthMetricEntry;
				const isBowel = healthEntry.metricType === 'bowel' || healthEntry.metricType === 'bowel_movement';
				return (
					<>
						<div className="flex items-center gap-2 mb-2">
							<span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
								{getMetricTypeLabel(healthEntry.metricType)}
							</span>
							{healthEntry.time && (
								<span className="text-xs text-gray-500">
									{formatTime(healthEntry.time)}
								</span>
							)}
						</div>
						{isBowel ? (
							<p className="text-gray-900 font-medium">
								{healthEntry.hasBowelMovement === 'yes' ? 'Ya' : 'Tidak'}
							</p>
						) : (
							<p className="text-gray-900">{healthEntry.description}</p>
						)}
						{healthEntry.notes && (
							<p className="text-sm text-gray-500 mt-1 italic">{healthEntry.notes}</p>
						)}
					</>
				);
			}
			case 'visit': {
				const visitEntry = entry as MedicalVisit;
				return (
					<>
						<p className="text-gray-900 font-medium">{visitEntry.doctorName}</p>
						{visitEntry.specialty && (
							<p className="text-sm text-gray-600">{visitEntry.specialty}</p>
						)}
						{visitEntry.hospitalClinic && (
							<p className="text-sm text-gray-500">{visitEntry.hospitalClinic}</p>
						)}
						{visitEntry.diagnosis && (
							<p className="text-sm text-gray-700 mt-2">
								<span className="font-medium">Diagnosis:</span> {visitEntry.diagnosis}
							</p>
						)}
						{visitEntry.notes && (
							<p className="text-sm text-gray-500 mt-1 italic">{visitEntry.notes}</p>
						)}
					</>
				);
			}
			case 'prescription': {
				const prescriptionEntry = entry as Prescription;
				return (
					<>
						<p className="text-gray-900 font-medium">{prescriptionEntry.medicationName}</p>
						<div className="text-sm text-gray-600 mt-1 space-y-0.5">
							{prescriptionEntry.dosage && <p>Dosis: {prescriptionEntry.dosage}</p>}
							{prescriptionEntry.frequency && <p>Frekuensi: {prescriptionEntry.frequency}</p>}
							{prescriptionEntry.duration && <p>Durasi: {prescriptionEntry.duration}</p>}
						</div>
						{prescriptionEntry.notes && (
							<p className="text-sm text-gray-500 mt-1 italic">{prescriptionEntry.notes}</p>
						)}
					</>
				);
			}
			case 'activity': {
				const activityEntry = entry as ActivityEntry;
				return (
					<>
						<p className="text-gray-900 font-medium">{activityEntry.activityName}</p>
						<p className="text-sm text-gray-600 mt-1">
							Durasi: {activityEntry.durationMinutes} menit
						</p>
						{activityEntry.notes && (
							<p className="text-sm text-gray-500 mt-1 italic">{activityEntry.notes}</p>
						)}
					</>
				);
			}
			default:
				return null;
		}
	};

	return (
		<div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
			<div className="flex justify-between items-start">
				<div className="flex-1">{renderContent()}</div>
				{canModify && (
					<div className="flex gap-2 ml-4">
						<button
							onClick={() => onEdit(entry, entryType)}
							className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
							aria-label="Edit"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
							</svg>
						</button>
						<button
							onClick={() => onDelete(entry, entryType)}
							className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
							aria-label="Hapus"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
					</div>
				)}
			</div>

			{/* Image Thumbnails */}
			{images.length > 0 && (
				<div className="mt-3 pt-3 border-t border-gray-100">
					<ImageGrid
						images={images}
						columns={3}
						size="sm"
						onImageClick={(index) => setLightboxIndex(index)}
					/>
				</div>
			)}

			<div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
				{new Date(entry.createdAt).toLocaleTimeString('id-ID', {
					hour: '2-digit',
					minute: '2-digit',
				})}
			</div>

			{/* Image Lightbox */}
			<ImageLightbox
				images={images}
				initialIndex={lightboxIndex ?? 0}
				isOpen={lightboxIndex !== null}
				onClose={() => setLightboxIndex(null)}
			/>
		</div>
	);
}
